import { connect } from 'react-redux';
import { compose, withHandlers, lifecycle, withState } from 'recompose';
import { injectIntl } from 'react-intl';
import {
  untouch,
  change as changeFieldValue,
  getFormValues,
  submit,
  hasSubmitFailed,
  getFormSyncErrors,
  touch,
} from 'redux-form';
import { get, isEmpty, head } from 'lodash';
import moment from 'moment';

import checkIsMobile from 'shared/utils/checkMobileDevice';
import diff from 'shared/utils/object-diff';
import { scrollToFirstError } from 'shared/utils/formTools';
import { checkExternalInitialization } from 'shared/utils/externalLoginUtils';
import { withRef } from 'shared/hocs/withRef';
import { UPDATE_APPOINTMENT_FORM } from 'modules/appointmentsManagement/constants/formTypes';
import {
  getAppointmentProvidersOptions,
  cantChangeProviderDueToConversationStatus,
  getAppointmentDetailsInitialValues,
  getAppointmentStateOptions,
  getTimeSlotsOptions,
  getFlexibleFromSlotsOptions,
  getFlexibleToSlotsOptions,
  appointmentUpdateFormFieldLabelsModified,
  getUpdateCurrentAppointmentPermissions,
} from 'modules/appointmentsManagement/selectors/appointments';
import { REQUIRED } from 'modules/appointmentsManagement/constants/providerSelection';
import { appointmentEditableStatusMessagesMap, CANCELED, ONGOING } from 'modules/common/constants/appointmentStatuses';
import { invokeFetchedByAnotherProviderModal, invokeUpdateGroupedAppointmentConfirmationModal } from 'modules/appointmentsManagement/actions/invokeModals';
import { submitUpdateAppointmentForm } from 'modules/appointmentsManagement/actions/updateAppointment';
import { getOperationHours } from 'modules/appointmentsManagement/actions/_api/fetchOperationHours';
import { fetchCalendarTimeSlots, fetchTimeSlots } from 'modules/appointmentsManagement/actions/_api/fetchTimeSlots';
import { fetchAppointmentServiceProviders } from 'modules/appointmentsManagement/actions/_api/fetchAppointmentServiceProviders';
import {
  clearOperationHours,
  clearTimeSlots,
  clearAppointmentServiceProviders,
  clearCalendarTimeSlots,
} from 'modules/appointmentsManagement/actions/createAppointment';
import {
  shouldDisableDate,
  shouldDisableEndDate,
  formatFlexibleInputChange,
  onProviderChangeCommon,
  isValidSpecialtyGroupProvider
} from 'modules/appointmentsManagement/utils/appontmentFormUtils';
import {
  getCloseAppointmentPermissions,
  getReadCurrentAppointmentPermissions,
} from 'modules/appointmentInfo/selectors/currentAppointment';
import { conferenceInProgressWithSIP, isInWaitingRoom } from 'modules/conference/selectors/consultationInfo';
import { WALK_IN, SCHEDULED } from 'modules/queues/constants/queueTypes';
import { FLEXIBLE, TIME_SLOT } from 'modules/appointmentsManagement/constants/schedulingTypes';
import {
  getApptCalendarTimeSlots,
  getApptTimeSlots,
  getConversationTypesOptions,
} from 'modules/appointmentsManagement/selectors/contracts';
import { getCommonUserConfig } from 'modules/user/selectors/users';
import createValidator from 'shared/utils/formValidator';
import * as userErrorCodes from 'modules/user/constants/localErrorCodes';
import {
  localErrorMessagesMap,
  EMPTY_SELECT_FIELD,
  EMPTY_TEXT_FIELD,
} from 'modules/appointmentsManagement/constants/localErrorCodes';

import EditAppointmentForm from './EditAppointmentForm';

const mapStateToProps = (state, { intl }) => {
  const form = UPDATE_APPOINTMENT_FORM;
  const appointment = get(state, 'appointmentInfo.currentAppointment', {});
  const serviceConfig = get(appointment, 'service.config', {});
  const formValues = getFormValues(form)(state) || {};
  const initialValues = getAppointmentDetailsInitialValues(state);
  const formErrors = getFormSyncErrors(form)(state);
  const submitFailed = hasSubmitFailed(form)(state);
  const customerHasError = submitFailed && !isEmpty(formErrors['customer']);
  const patientHasError = submitFailed && !isEmpty(formErrors['patient']);
  const { hasCancelPermission } = getCloseAppointmentPermissions(state);
  const updatePermissions = getUpdateCurrentAppointmentPermissions(state);
  const readPermissions = getReadCurrentAppointmentPermissions(state);

  return {
    form,
    formValues,
    serviceConfig,
    initialValues,
    customerHasError,
    patientHasError,
    requestStatus: state.appointmentsManagement.updateAppointment.requestStatus,
    appointmentId: appointment.appointmentId,
    contractId: appointment.contract.contractId,
    isOngoingAppointment: appointment.status === ONGOING,
    providersStatus: state.appointmentsManagement.appointmentConfig.providersStatus,
    providersOptions: getAppointmentProvidersOptions(
      state,
      intl,
      serviceConfig.providerSelectionType === REQUIRED,
      serviceConfig.queueType === WALK_IN,
    ),
    cantChangeProviderDueToConversationStatus: cantChangeProviderDueToConversationStatus(state),
    userIsOnCall: conferenceInProgressWithSIP(state) || isInWaitingRoom(state),
    fetchedBy: get(appointment, 'attributes.fetchedBy'),
    conversationTypesOptions: getConversationTypesOptions(intl, { service: appointment.service }),
    operationHours: state.appointmentsManagement.appointmentConfig.operationHours,
    timeSlots: getApptTimeSlots(state),
    timeSlotsStatus: state.appointmentsManagement.appointmentConfig.timeSlotsStatus,
    timeSlotsOptions: getTimeSlotsOptions(state, formValues),
    flexibleFromSlotsOptions: getFlexibleFromSlotsOptions(state, serviceConfig, get(formValues, 'date'), get(formValues, 'providerUserId')),
    flexibleToSlotsOptions: getFlexibleToSlotsOptions(state, serviceConfig, get(formValues, 'flexible.from'), get(formValues, 'endDate'), get(formValues, 'providerUserId')), // eslint-disable-line
    appointmentStatusOptions: Object.entries(appointmentEditableStatusMessagesMap).map(([key, value]) => ({ key, value: intl.formatMessage(value) })),
    appointmentStateOptions: getAppointmentStateOptions(state, intl),
    canEditStatus: hasCancelPermission,
    isMobile: checkIsMobile(),
    isExternalInit: checkExternalInitialization(),
    appointmentFormFieldLabelsModified: appointmentUpdateFormFieldLabelsModified(serviceConfig),
    isGroupedAppointment: !!state.appointmentInfo.currentAppointment.group,
    updatePermissions,
    readPermissions,
    userFieldConfig: getCommonUserConfig(state),
    calendarTimeSlots: getApptCalendarTimeSlots(state),
    calendarTimeSlotsStatus: state.appointmentsManagement.appointmentConfig.calendarTimeSlotsStatus,
  };
};

const submitForm = () => submit(UPDATE_APPOINTMENT_FORM);
const touchForm = (errors) => touch(UPDATE_APPOINTMENT_FORM, errors);

const mapDispatchToProps = {
  untouch,
  changeFieldValue,
  fetchTimeSlots,
  clearTimeSlots,
  getOperationHours,
  clearOperationHours,
  fetchAppointmentServiceProviders,
  clearAppointmentServiceProviders,
  submitForm,
  touchForm,
  submitUpdateAppointmentForm,
  invokeFetchedByAnotherProviderModal,
  invokeUpdateGroupedAppointmentConfirmationModal,
  fetchCalendarTimeSlots,
  clearCalendarTimeSlots,
};

export default compose(
  injectIntl,
  withRef,
  connect(mapStateToProps, mapDispatchToProps),
  withHandlers({
    onProviderChangeCommon,
  }),
  withState('providerChangeWarnShown', 'changeProviderChangeWarnShown', false),
  withHandlers({
    onSubmitFail: ({ getRef, touchForm }) => (errors) => {
      scrollToFirstError(errors, getRef, (error) => touchForm([error]));
    },
    onFlexibleFromChange: (
      {
        serviceConfig: { fixedAppointmentDuration, maxAppointmentDuration, defaultAppointmentDuration },
        changeFieldValue,
        form,
        formValues: { flexible, endDate, date },
        timeSlots,
      }) => (value) => {
      let endTime = null;

      if (fixedAppointmentDuration && value) {
        endTime = moment(value)
          .add(fixedAppointmentDuration, 'm')
          .valueOf();

        if (!moment(endDate).isSame(endTime, 'day')) {
          const currentSlot = timeSlots.find(item => item.from === value || moment(value).isBetween(item.from, item.to));
          if (currentSlot.to < endTime && endTime > endDate) {
            return changeFieldValue(form, 'endDate', moment(currentSlot.to).endOf('day').valueOf());
          }

          changeFieldValue(form, 'endDate', moment(endTime > endDate ? endTime : date).endOf('day').valueOf());
        }
      }

      if (maxAppointmentDuration && value) {
        const maxTime = moment(value)
          .add(maxAppointmentDuration, 'm')
          .valueOf();

        if (!moment(endDate).isSame(maxTime, 'day') && maxTime < endDate) {
          changeFieldValue(form, 'endDate', moment(date).endOf('day').valueOf());
        }
      }

      if (defaultAppointmentDuration && value) {
        endTime = moment(value)
          .add(defaultAppointmentDuration, 'm')
          .valueOf();

        if (!moment(endDate).isSame(endTime, 'day')) {
          changeFieldValue(form, 'endDate', moment(endTime > endDate ? endTime : date).endOf('day').valueOf());
        }
      }

      if (flexible && value && flexible.to && value < flexible.to && !(fixedAppointmentDuration || maxAppointmentDuration)) return;

      changeFieldValue(form, 'flexible.to', endTime);
    },
  }),
  withHandlers({
    setFirstAvailableSlot: ({ form, onFlexibleFromChange, flexibleFromSlotsOptions, changeFieldValue }) => () => {
      const value = get(head(flexibleFromSlotsOptions), 'key');
      changeFieldValue(form, 'flexible.from', value);
      onFlexibleFromChange(value);
    },
  }),
  withHandlers({
    onProviderChangeCommon,
    onScheduledDateChange:
      ({
         clearTimeSlots,
         fetchTimeSlots,
         contractId,
         changeFieldValue,
         form,
         formValues: { date, providerUserId },
         appointmentId,
         setFirstAvailableSlot,
       }) => (momentDate, isFlexible, isStartDate) => {
          clearTimeSlots();
          if (!isFlexible) {
            fetchTimeSlots(contractId, momentDate, null, providerUserId, appointmentId).then(({ payload }) => {
              if (!isEmpty(payload)) {
                const { from, to } = payload[0];
                changeFieldValue(form, 'timeSlot', `${ from }-${ to }`);
              }
            });
          } else if (isStartDate) {
            const toDate = momentDate.clone().endOf('day');

            fetchTimeSlots(contractId,
              momentDate,
              toDate.clone().add(1, 'day'),
              providerUserId,
              appointmentId,
            ).then(() => setTimeout(setFirstAvailableSlot, 500));

            changeFieldValue(form, 'endDate', toDate);
          } else {
            const fromDate = moment(date);
            fetchTimeSlots(contractId, fromDate, momentDate, providerUserId, appointmentId);
          }
          changeFieldValue(form, 'flexible', null);
          changeFieldValue(form, 'timeSlot', null);
        },
  }),
  withHandlers({
    shouldDisableDate,
    shouldDisableEndDate,
    formatFlexibleInputChange,

    onProviderChange: ({
      form,
      changeFieldValue,
      onScheduledDateChange,
      onProviderChangeCommon,
      contractId,
      appointmentId,
      serviceConfig: { schedulingType, queueType, providerSelectionType },
      formValues: { providerUserId },
      fetchCalendarTimeSlots,
    }) => value => {
      if ((!value && providerUserId === 'PROVIDERS_GROUP') || (value === 'PROVIDERS_GROUP' && !providerUserId) || isValidSpecialtyGroupProvider(value)) return;
      onProviderChangeCommon();

      if ((!value && providerSelectionType === REQUIRED) || queueType === WALK_IN) return;

      fetchCalendarTimeSlots(contractId, moment(), value, appointmentId).then(({ payload }) => {
        if (isEmpty(payload)) return;

        changeFieldValue(form, 'date', moment(new Date(payload[0].from)));

        if (schedulingType === TIME_SLOT) {
          onScheduledDateChange(moment(new Date(payload[0].from)));
        }

        if (schedulingType === FLEXIBLE) {
          onScheduledDateChange(moment(new Date(payload[0].from)), true, true);
        }
      });
    },

    onMonthChange: ({ fetchCalendarTimeSlots, contractId, formValues: { providerUserId }, appointmentId }) => momentDate =>
      fetchCalendarTimeSlots(contractId, momentDate.clone().startOf('month'), providerUserId, appointmentId),
  }),
  withHandlers({
    onSubmit: ({
      initialValues,
      isMobile,
      isExternalInit,
      fetchedBy,
      serviceConfig,
      submitUpdateAppointmentForm,
      cantChangeProviderDueToConversationStatus,
      providerChangeWarnShown,
      changeProviderChangeWarnShown,
      invokeFetchedByAnotherProviderModal,
      invokeUpdateGroupedAppointmentConfirmationModal,
      isGroupedAppointment,
    }) => async (formValues) => {
      const data = {
        appointmentId: formValues.appointmentId,
        ...diff(initialValues, formValues),
        customer: formValues.customer,
        patient: formValues.patient,
      };
      if (data.state) data.status = data.state;

      if (isGroupedAppointment) {
        const agree = await new Promise(resolve => invokeUpdateGroupedAppointmentConfirmationModal(resolve));
        if (!agree) return;
      }

      if (!providerChangeWarnShown && formValues.providerUserId && fetchedBy && serviceConfig.schedulingType !== FLEXIBLE && !cantChangeProviderDueToConversationStatus) {
        changeProviderChangeWarnShown(true);
        return invokeFetchedByAnotherProviderModal();
      }

      return submitUpdateAppointmentForm(data, (isMobile && isExternalInit));
    },
    validate: ({
      appointmentFormFieldLabelsModified,
      serviceConfig: { queueType, schedulingType, providerSelectionType },
      userFieldConfig = {},
      cancelReasonRequired,
      intl,
    }) => (formData) => {
      let rules = {
        'conversationType': ['required'],
      };
      let errorMessages = {
        'required.conversationType': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.providerUserId': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.date': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.timeSlot': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.flexible.from': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.flexible.to': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.state': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.cancelReason': localErrorMessagesMap[EMPTY_SELECT_FIELD],
        'required.priority': localErrorMessagesMap[EMPTY_SELECT_FIELD],
      };

      if (queueType !== WALK_IN) {
        rules['date'] = ['required'];

        if (schedulingType === TIME_SLOT) {
          rules['timeSlot'] = ['required'];
        } else {
          rules['flexible.from'] = ['required'];
          rules['flexible.to'] = ['required'];
        }
      }

      if (providerSelectionType === REQUIRED) {
        rules['providerUserId'] = ['required'];
      }

      if (formData.status === CANCELED) {
        rules['state'] = ['required'];

        if (formData.state === CANCELED && cancelReasonRequired) {
          rules['cancelReason'] = ['required'];
        }
      }

      ['customer', 'patient'].forEach(type => {
        rules = {
          ...rules,
          [`${ type }.firstName`]: ['string', 'max:200'],
          [`${ type }.lastName`]: ['string', 'max:200'],
          [`${ type }.middleName`]: ['max:200'],
          [`${ type }.title`]: [],
          [`${ type }.gender`]: [],
          [`${ type }.dateOfBirth`]: ['dateOfBirth'],
          [`${ type }.email`]: ['email'],
          [`${ type }.phoneNumber`]: ['telephone'],
          [`${ type }.externalId`]: [`regex:/${ userFieldConfig.externalIdRegexp }/`],
          [`${ type }.address`]: [],
          [`${ type }.description`]: [],
        };

        get(userFieldConfig, 'requiredFields', []).forEach(field => {
          if (rules[`${ type }.${ field }`]) rules[`${ type }.${ field }`].unshift('required');
        });

        errorMessages = {
          ...errorMessages,
          [`required.${ type }.firstName`]: `${ userErrorCodes.EMPTY_USER_NAME_LOCAL }`,
          [`required.${ type }.lastName`]: `${ userErrorCodes.EMPTY_USER_LAST_NAME_LOCAL }`,
          [`required.${ type }.middleName`]: localErrorMessagesMap[EMPTY_TEXT_FIELD],
          [`required.${ type }.title`]: localErrorMessagesMap[EMPTY_SELECT_FIELD],
          [`required.${ type }.gender`]: localErrorMessagesMap[EMPTY_SELECT_FIELD],
          [`required.${ type }.email`]: `${ userErrorCodes.EMPTY_USER_EMAIL_LOCAL }`,
          [`required.${ type }.phoneNumber`]: `${ userErrorCodes.EMPTY_USER_PHONE_LOCAL }`,
          [`required.${ type }.dateOfBirth`]: `${ userErrorCodes.EMPTY_DATE_OF_BIRTH_LOCAL }`,
          [`required.${ type }.externalId`]: userErrorCodes[userErrorCodes.EMPTY_USERID],
          [`required.${ type }.address`]: localErrorMessagesMap[EMPTY_TEXT_FIELD],
          [`required.${ type }.description`]: localErrorMessagesMap[EMPTY_TEXT_FIELD],
          [`invalid.${ type }.email`]: `${ userErrorCodes.INVALID_FORMAT_USER_EMAIL_LOCAL }`,
          [`invalid.${ type }.phoneNumber`]: `${ userErrorCodes.INVALID_FORMAT_USER_PHONE_LOCAL }`,
          [`invalid.${ type }.dateOfBirth`]: `${ userErrorCodes.INVALID_DATE_OF_BIRTH_LOCAL }`,
          [`regex.${ type }.externalId`]: `${ userErrorCodes.INVALID_EXTERNAL_ID }`,
        };
      });

      return createValidator(rules, errorMessages, appointmentFormFieldLabelsModified)(formData, { intl });
    },
  }),
  lifecycle({
    componentDidMount() {
      const {
        contractId,
        serviceConfig: { queueType, schedulingType },
        formValues: { date, endDate, providerUserId },
        initialValues: { date: initDate, endDate: initEndDate, providerUserId: initProviderUserId },
        appointmentId,
      } = this.props;

      if (queueType === SCHEDULED) {
        this.props.getOperationHours(contractId);
        this.props.fetchCalendarTimeSlots(
          contractId,
          moment(date || initDate).startOf('month'),
          initProviderUserId || providerUserId,
          appointmentId,
        );
        this.props.fetchTimeSlots(
          contractId,
          moment(date || initDate),
          schedulingType === FLEXIBLE ? moment(endDate || initEndDate).add(1, 'day') : null,
          initProviderUserId || providerUserId,
          appointmentId,
        );
      }
      this.props.fetchAppointmentServiceProviders();
    },
  }),
)(EditAppointmentForm);
